import { useCallback, useEffect, useState } from "react";
import { admin, type JobView } from "../../api/admin";
import { ParamForm } from "./ParamForm";
import { Counters, Progress, StateBadge, took, when, StopRun } from "./bits";

/**
 * One card per stage: what it does, what it writes, when it last ran, and the
 * three things the panel exists for -- run it, stop it, change its settings.
 */
export function Jobs({ onWatch }: { onWatch: (runId: number) => void }) {
  const [jobs, setJobs] = useState<JobView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const load = useCallback(() => {
    admin.jobs().then(setJobs).catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    load();
    // Something is usually running when this page is open, and its progress is
    // the reason to be looking at it.
    const t = setInterval(load, 2000);
    return () => clearInterval(t);
  }, [load]);

  if (error) return <div className="error">{error}</div>;
  if (!jobs) return <div className="loading"><span className="spinner" />loading…</div>;

  return (
    <div className="jobs">
      {jobs.map((j) => (
        <JobCard
          key={j.kind}
          job={j}
          open={open === j.kind}
          onToggle={() => setOpen(open === j.kind ? null : j.kind)}
          onChanged={load}
          onWatch={onWatch}
        />
      ))}
    </div>
  );
}

function JobCard({
  job: j, open, onToggle, onChanged, onWatch,
}: {
  job: JobView;
  open: boolean;
  onToggle: () => void;
  onChanged: () => void;
  onWatch: (runId: number) => void;
}) {
  const [values, setValues] = useState<Record<string, unknown>>(j.defaults);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Reopening the form should show what a run would actually use now, not what
  // it would have used when the page loaded.
  useEffect(() => { if (open) setValues(j.defaults); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const running = j.running;

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true); setErr(null);
    try { await fn(); onChanged(); }
    catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <section className={`job${j.available ? "" : " planned"}`}>
      <header>
        <div>
          <h3>
            {j.title}
            <code>{j.kind}</code>
            {!j.available && <span className="soon">unavailable</span>}
          </h3>
          <p className="summary">{j.summary}</p>
          {!j.available && j.note && <p className="note">{j.note}</p>}
        </div>

        <div className="actions">
          {running ? (
            <StopRun run={running} onDone={onChanged} />
          ) : (
            <button
              className="primary"
              disabled={!j.available || busy}
              onClick={() =>
                act(async () => {
                  const { runId } = await admin.run(j.kind, values);
                  onWatch(runId);
                })
              }
            >
              Run
            </button>
          )}
          {j.params.length > 0 && (
            <button onClick={onToggle}>{open ? "Hide settings" : "Settings"}</button>
          )}
        </div>
      </header>

      {(j.writes?.length || j.hosts?.length) && (
        <div className="facts">
          {j.writes?.length ? <span>writes <b>{j.writes.join(", ")}</b></span> : null}
          {j.hosts?.length ? <span>talks to <b>{j.hosts.join(", ")}</b></span> : null}
        </div>
      )}

      {running && (
        <div className="live" onClick={() => onWatch(running.id)}>
          <StateBadge state={running.state} />
          <Progress run={running} />
          <Counters counters={running.counters} />
          <span className="watch">watch the log →</span>
        </div>
      )}

      {!running && j.last && (
        <div className="last">
          <StateBadge state={j.last.state} />
          <button className="link" onClick={() => onWatch(j.last!.id)}>
            run {j.last.id}
          </button>
          <span className="dim">
            {when(j.last.finishedAt)} · took {took(j.last)} · by {j.last.triggeredBy}
          </span>
          <Counters counters={j.last.counters} />
          {j.last.error && <span className="failmsg">{j.last.error}</span>}
        </div>
      )}

      {j.nextRun && <div className="next">next scheduled run {when(j.nextRun)}</div>}

      {open && (
        <div className="settings">
          <ParamForm
            specs={j.params}
            values={values}
            disabled={busy}
            onChange={(name, v) => { setValues((p) => ({ ...p, [name]: v })); setSaved(false); }}
          />
          <div className="settings-actions">
            <button
              disabled={busy}
              onClick={() =>
                act(async () => {
                  await admin.saveDefaults(j.kind, values);
                  setSaved(true);
                })
              }
              title="what a one-click or scheduled run will use from now on"
            >
              Save as default
            </button>
            <button disabled={busy} onClick={() => setValues(j.defaults)}>Revert</button>
            {saved && <span className="ok">saved</span>}
          </div>
        </div>
      )}

      {err && <div className="error">{err}</div>}
    </section>
  );
}
